(function () {
  const THEME_CART_HEADER = 'x-theme-cart-request';
  // Matches both Ajax API shapes theme/third-party code use for cart mutations:
  // the JSON suffix form (/cart/add.js) and the bare route form (/cart/add), the
  // latter used by window.routes.cart_add_url et al. (see layout/theme.liquid).
  // Tested against a URL's pathname only, which never carries a query string,
  // so anchoring on end-of-string ($) is safe here.
  const CART_MUTATION_PATTERN = /\/cart\/(?:add|change|update|clear)(?:\.js)?$/i;
  const REFRESH_DEBOUNCE_MS = typeof ON_CHANGE_DEBOUNCE_TIMER !== 'undefined' ? ON_CHANGE_DEBOUNCE_TIMER : 300;

  // Captured at install time so the chain keeps working no matter what else patched
  // window.fetch before us (e.g. LoyaltyLionDiscountHandler in global.js).
  const previousFetch = typeof window.fetch === 'function' ? window.fetch.bind(window) : null;

  let refreshTimer = null;
  let isRefreshing = false;
  let refreshQueued = false;

  // Seeded from Liquid (layout/theme.liquid) so the very first mutation observed on a page
  // load already has something to compare against. Left null when the seed is missing (e.g. a
  // layout that doesn't render it): a null baseline never counts as a gain, it only starts
  // tracking from that point on — see SHOPSUP-3660.
  let lastKnownItemCount = typeof window.cartItemCount === 'number' ? window.cartItemCount : null;

  // Bumped on every authoritative write to lastKnownItemCount. The async re-read at the bottom
  // of this file captures the generation it started under and discards its own (by then stale)
  // result if anything moved the baseline in the meantime, so a late response can never drag
  // the baseline back below the real cart — which would make the next mutation read as a gain.
  let itemCountGeneration = 0;

  function setKnownItemCount(count) {
    lastKnownItemCount = typeof count === 'number' ? count : null;
    itemCountGeneration += 1;
  }

  function extractUrl(input) {
    if (typeof input === 'string') return input;
    if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
    if (typeof URL !== 'undefined' && input instanceof URL) return input.href;
    return input ? String(input) : '';
  }

  function isSameOriginCartMutation(input) {
    const rawUrl = extractUrl(input);
    // Cheap bail-out first: this runs on every fetch/XHR in the page.
    if (!rawUrl || rawUrl.indexOf('/cart/') === -1) return false;

    let parsed;
    try {
      parsed = new URL(rawUrl, window.location.href);
    } catch (error) {
      return false;
    }

    return parsed.origin === window.location.origin && CART_MUTATION_PATTERN.test(parsed.pathname);
  }

  function headersHaveThemeFlag(headers) {
    if (!headers) return false;
    if (typeof Headers !== 'undefined' && headers instanceof Headers) return headers.has(THEME_CART_HEADER);
    if (Array.isArray(headers)) return headers.some(([key]) => key?.toLowerCase() === THEME_CART_HEADER);
    return Object.keys(headers).some((key) => key.toLowerCase() === THEME_CART_HEADER);
  }

  function isThemeRequest(input, init) {
    if (init?.headers && headersHaveThemeFlag(init.headers)) return true;
    if (typeof Request !== 'undefined' && input instanceof Request) return headersHaveThemeFlag(input.headers);
    return false;
  }

  function getRefreshTargets() {
    const cartDrawerEl = document.querySelector('cart-drawer');
    if (cartDrawerEl && typeof cartDrawerEl.getSectionsToRender === 'function') {
      const sections = cartDrawerEl.getSectionsToRender();
      // renderContents() repaints #CartDrawer, so only drive it when that node is on the page.
      if (sections.some((section) => section.id === 'cart-drawer')) return { cartDrawerEl, sections };
      return { cartDrawerEl: null, sections };
    }
    if (document.getElementById('cart-icon-bubble')) {
      return { cartDrawerEl: null, sections: [{ id: 'cart-icon-bubble' }] };
    }
    return { cartDrawerEl: null, sections: [] };
  }

  function renderCartIconBubbleOnly(sectionHtml) {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || !sectionHtml) return;

    const parsedSection = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('.shopify-section');
    if (parsedSection) bubble.innerHTML = parsedSection.innerHTML;
  }

  // Gorgias does not ask for bundled sections in its cart call, and GET /cart.js does not
  // accept a `sections` parameter, so the cart state and the rendered sections have to be
  // requested separately: the Ajax API for the state, the Section Rendering API for the HTML.
  function fetchCartState() {
    return previousFetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then(
      (response) => {
        if (!response.ok) throw new Error(`cart.js responded ${response.status}`);
        return response.json();
      }
    );
  }

  function fetchRenderedSections(sectionIds) {
    // Rendered in the context of the current page, which already carries the locale prefix.
    const url = `${window.location.pathname}?sections=${encodeURIComponent(sectionIds.join(','))}`;
    return previousFetch(url).then((response) => {
      if (!response.ok) throw new Error(`section rendering responded ${response.status}`);
      // The Section Rendering API returns a flat { '<section-id>': '<html>' } map.
      return response.json();
    });
  }

  async function refresh() {
    const targets = getRefreshTargets();
    if (!targets.sections.length) return;

    const sectionIds = targets.sections.map((section) => section.id);

    let cartJson;
    let renderedSections;
    try {
      [cartJson, renderedSections] = await Promise.all([fetchCartState(), fetchRenderedSections(sectionIds)]);
    } catch (error) {
      console.error('gorgias-cart-sync: could not refresh the cart', error);
      return;
    }

    // A section id that does not resolve comes back as null; renderContents() would throw on it.
    const missing = sectionIds.filter((id) => typeof renderedSections?.[id] !== 'string');
    if (missing.length) {
      console.error(`gorgias-cart-sync: missing rendered section(s): ${missing.join(', ')}`);
      return;
    }

    const parsedState = { ...cartJson, sections: renderedSections };

    // The drawer only auto-opens when the cart gains items over the last known count. A null
    // baseline (unseeded) or an equal/lower count never opens it, which is what keeps a
    // third-party app writing cart attributes on page load (SHOPSUP-3660) from popping the
    // drawer, while a real add-to-cart (Gorgias or the theme) still does.
    const shouldOpen =
      lastKnownItemCount !== null &&
      typeof cartJson.item_count === 'number' &&
      cartJson.item_count > lastKnownItemCount;
    if (typeof cartJson.item_count === 'number') setKnownItemCount(cartJson.item_count);

    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gorgias', cartData: parsedState });

    if (targets.cartDrawerEl) {
      if (shouldOpen) {
        // Opened programmatically, so there is no triggering element: give close() something
        // sensible to return focus to.
        targets.cartDrawerEl.setActiveElement(document.getElementById('cart-icon-bubble') || document.body);
      }
      targets.cartDrawerEl.renderContents(parsedState, shouldOpen);
      // The host <cart-drawer> sits outside #CartDrawer (snippets/cart-drawer.liquid:25), so
      // renderContents() — which only replaces #CartDrawer's innerHTML — never repaints its class
      // attribute. Everything inside, cart-drawer-items included, comes back from the Section
      // Rendering API already carrying the right state; the host is the one node that has to be
      // corrected by hand, on both the opening and the silent branch.
      //
      // Toggle, not remove, mirroring the theme's own handling in cart.js:393. An external
      // mutation that empties the cart has to put is-empty *back*: otherwise opening the drawer
      // by hand afterwards shows the free-shipping bar and the upsell with no empty-cart copy and
      // no checkout footer (SHOPSUP-3660).
      targets.cartDrawerEl.classList.toggle('is-empty', cartJson.item_count === 0);
    } else {
      renderCartIconBubbleOnly(parsedState.sections['cart-icon-bubble']);
    }
  }

  function runRefresh() {
    refreshTimer = null;
    if (isRefreshing) {
      refreshQueued = true;
      return;
    }

    isRefreshing = true;
    refresh().finally(() => {
      isRefreshing = false;
      if (refreshQueued) {
        refreshQueued = false;
        scheduleRefresh();
      }
    });
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(runRefresh, REFRESH_DEBOUNCE_MS);
  }

  if (previousFetch) {
    window.fetch = (input, init) => {
      const request = previousFetch(input, init);

      if (!isSameOriginCartMutation(input) || isThemeRequest(input, init)) {
        return request;
      }

      request
        .then((response) => {
          if (!response?.ok) return;

          // Registered before the caller's own handlers, so the body is still unread.
          response
            .clone()
            .json()
            .then((data) => {
              // Bundled sections in the response mean the caller already repaints itself.
              if (data?.sections) return;
              scheduleRefresh();
            })
            .catch(() => {
              scheduleRefresh();
            });
        })
        .catch(() => {});

      return request;
    };
  }

  const originalXhrOpen = XMLHttpRequest.prototype.open;
  const originalXhrSend = XMLHttpRequest.prototype.send;
  const originalXhrSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__gorgiasCartSyncUrl = url;
    this.__gorgiasCartSyncIsThemeRequest = false;
    return originalXhrOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
    if (name?.toLowerCase() === THEME_CART_HEADER) this.__gorgiasCartSyncIsThemeRequest = true;
    return originalXhrSetRequestHeader.call(this, name, value);
  };

  XMLHttpRequest.prototype.send = function (...args) {
    if (isSameOriginCartMutation(this.__gorgiasCartSyncUrl) && !this.__gorgiasCartSyncIsThemeRequest) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) scheduleRefresh();
      });
    }
    return originalXhrSend.apply(this, args);
  };

  // Theme-originated mutations carry X-Theme-Cart-Request and are excluded from the
  // fetch/XHR interceptors above by design (see isThemeRequest), so this subscription is the
  // only way their item_count reaches lastKnownItemCount. Without it the counter would lag
  // behind after a theme add-to-cart and a later external mutation could look like a false
  // gain.
  //
  // The payload shape is not homogeneous: cart.js, quick-add-bulk.js and quick-order-list.js
  // publish the full cart (item_count included), but the two add-to-cart paths
  // — product-form.js and component-product-quick-add.js — publish the raw /cart/add.js
  // response, which is the added line item and carries no total. Keeping the old number in
  // that case would leave the baseline behind the real cart and the next external mutation
  // would read as a gain, so an unknown total blanks the baseline first and then re-reads the
  // real count from the Ajax API, which is what lets the *next* external gain still open the
  // drawer instead of being swallowed as "baseline unknown".
  //
  // The handle is held rather than discarded on purpose. This file is an IIFE that patches
  // window.fetch and XMLHttpRequest permanently — there is no disconnectedCallback to unhook
  // from — so the subscription is meant to live for the whole page. Keeping the reference
  // means anything that ever does need to tear it down has one, instead of the subscription
  // being unreachable. It is deliberately never called here.
  // eslint-disable-next-line no-unused-vars
  const unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    const publishedItemCount = event?.cartData?.item_count;
    if (typeof publishedItemCount === 'number') {
      setKnownItemCount(publishedItemCount);
      return;
    }

    // Blank it synchronously: until the re-read lands the baseline is genuinely unknown, and a
    // stale number left in place here is exactly what would reopen the drawer on the next
    // external mutation (SHOPSUP-3660).
    setKnownItemCount(null);

    const generation = itemCountGeneration;
    // GET /cart.js does not match CART_MUTATION_PATTERN and goes out through the captured
    // previousFetch, so this can neither re-enter our own interceptor nor loop. Wrapped in a
    // resolved promise so a missing window.fetch surfaces as a rejection rather than throwing
    // out of the subscriber and breaking publish() for everyone downstream.
    Promise.resolve()
      .then(fetchCartState)
      .then((cart) => {
        // Something authoritative landed while this was in flight (a refresh(), or another
        // publish): that number is newer than ours, so leave it alone.
        if (generation !== itemCountGeneration) return;
        if (typeof cart?.item_count === 'number') setKnownItemCount(cart.item_count);
      })
      .catch(() => {
        // Baseline is already null, i.e. never a gain. The next refresh() adopts the real count
        // silently and tracking resumes from there.
      });
  });

  window.themeCartSync = { refresh: scheduleRefresh, refreshNow: refresh };
})();
